import type {MenuItem} from 'shared/components/kp-main-menu/types';
import type {Readable} from 'svelte/store';
import type {KpMainMenuService} from 'shared/components/kp-main-menu/kp-main-menu.service';
import {getContext, setContext} from 'svelte';
import {writable} from 'svelte/store';
import {calculateRgbLuminance, parseCssColorToRgb} from 'shared/components/kp-main-menu/color-util';
import {exists} from 'shared/utils/custom-utils';

const mainMenuContextKey = 'kp-main-menu-ctx';
const searchFadeAnimDuration = 125;
type MainMenuTheme = 'light' | 'dark';

interface KpMainMenuContext {
    service: KpMainMenuService;
    eventBus: EventTarget;
    backgroundColor: string;
    textColor: string;
    highlightBackgroundColor: string;
    highlightTextColor: string;
    backgroundTheme: MainMenuTheme;
    items: Readable<MenuItem[]>;
    isStuckToTop: Readable<boolean>;
    isNavSearchOpen: Readable<boolean>;
    searchFadeAnimDuration: number;
    updateIsStuckToTop: (topOffset: number) => void;
    toggleNavSearchOpen: (value: boolean) => void;
    updateMenuItems: (newMenuItems: MenuItem[]) => void;
}

export function createMainMenuContext(service: KpMainMenuService): KpMainMenuContext {
    const eventBus = new EventTarget();
    const menuSettings = service.getMenuSettings();
    const isStuckToTop = writable<boolean>(false);
    const isNavSearchOpen = writable<boolean>(false);
    const calculatedBackgroundTheme = calculateBackgroundTheme(menuSettings.backgroundColor);
    const backgroundTheme = calculatedBackgroundTheme ?? 'light';
    const highlightBackgroundColor = menuSettings.highlightBackgroundColor ?? (exists(calculatedBackgroundTheme) ? getHighlightColor(calculatedBackgroundTheme, 0.2) : menuSettings.textColor);
    const highlightTextColor = menuSettings.highlightTextColor ?? menuSettings.textColor;
    const items = writable<MenuItem[]>(menuSettings.items);

    return setContext<KpMainMenuContext>(mainMenuContextKey, {
        service,
        eventBus,
        backgroundColor: menuSettings.backgroundColor,
        textColor: menuSettings.textColor,
        backgroundTheme,
        items,
        highlightBackgroundColor,
        highlightTextColor,
        isStuckToTop,
        isNavSearchOpen,
        searchFadeAnimDuration,
        updateIsStuckToTop: (topOffset) => isStuckToTop.set(topOffset <= 0),
        toggleNavSearchOpen: (value) => isNavSearchOpen.set(value),
        updateMenuItems: (newMenuItems: MenuItem[]) => items.set(newMenuItems)
    });
}

export function getMainMenuContext(): KpMainMenuContext {
    return getContext<KpMainMenuContext>(mainMenuContextKey);
}

function calculateBackgroundTheme(color: string): MainMenuTheme | null {
    const rgb = parseCssColorToRgb(color);
    if (!exists(rgb)) {
        return 'dark';
    }

    const luminance = calculateRgbLuminance(rgb);
    return luminance > 186 ? 'light' : 'dark';
}

function getHighlightColor(theme: MainMenuTheme, intensity: number): string {
    if (theme === 'dark') {
        return `rgba(255, 255, 255, ${intensity})`;
    }

    return `rgba(30, 30, 36, ${intensity - 0.05})`;
}